import React, { useState } from 'react';
import '../styles/PeerReviewProcess.css';

const PeerReviewProcess = () => {
  const [expandedStep, setExpandedStep] = useState(null);

  const steps = [
    {
      id: 1,
      title: 'Initial Screening',
      icon: '📋',
      content: 'All submissions undergo an initial screening to ensure completeness, adherence to formatting guidelines, and compliance with ethical standards. Each manuscript is subjected to plagiarism detection, including screening for AI-generated content. The similarity index must be below 10% to be eligible for peer review and publication. Manuscripts exceeding this threshold may be rejected at any stage and will not be considered for inclusion in the conference proceedings.'
    },
    {
      id: 2,
      title: 'Reviewer Assignment',
      icon: '👥',
      content: 'The conference follows a double-anonymous peer-review process. Each submission is evaluated by two to three independent expert reviewers with relevant domain expertise. Reviewers are appointed by the Technical Program Committee (TPC) based on their subject-matter competence and the absence of any conflict of interest.'
    },
    {
      id: 3,
      title: 'Review Criteria',
      icon: '✓',
      criteria: [
        'Scope: Relevance of the manuscript to the conference theme and objectives',
        'Novelty: Originality and distinction from previously published work',
        'Technical Validity: Soundness of methodology, analysis, and execution',
        'Data Integrity: Accuracy and appropriateness of data reporting, analysis, and interpretation',
        'Clarity: Quality of presentation, organisation, and logical flow',
        'Compliance: Adherence to ethical, academic, and publication standards',
        'Contribution: Significance and potential impact of the work on the field'
      ]
    },
    {
      id: 4,
      title: 'Peer Review Decisions',
      icon: '🎯',
      decisions: [
        {
          title: 'Accept',
          desc: 'The paper is accepted without mandatory revisions. Authors may be requested to submit final camera-ready files.'
        },
        {
          title: 'Weak Accept with Revisions',
          desc: 'The paper is conditionally accepted, subject to satisfactory incorporation of reviewer comments. A revised manuscript must be submitted within the prescribed timeline.'
        },
        {
          title: 'Reject',
          desc: 'The paper is not accepted for presentation or publication in the conference proceedings. Authors are free to submit the work to another venue.'
        }
      ]
    },
    {
      id: 5,
      title: 'Decision Notification',
      icon: '📧',
      content: 'Authors receive formal notification of the decision along with anonymised reviewer feedback. Revised submissions must be uploaded within the stipulated deadline. Final acceptance is contingent upon successful revision and full compliance with all ethical and publication requirements.'
    }
  ];

  const toggleStep = (stepId) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  return (
    <section id="peer-review" className="peer-review-process">
      <div className="container">
        <h2 className="section-title">Peer Review Process</h2>
        <p className="section-subtitle">Ensuring Academic Integrity and Quality Standards</p>

        <div className="process-timeline">
          {steps.map((step) => (
            <div 
              key={step.id} 
              className={`process-step ${expandedStep === step.id ? 'expanded' : ''}`}
            >
              <div 
                className="step-header"
                onClick={() => toggleStep(step.id)}
              >
                <div className="step-icon">{step.icon}</div>
                <div className="step-title-wrapper">
                  <h3 className="step-number">Step {step.id}</h3>
                  <h4 className="step-title">{step.title}</h4>
                </div>
                <button className="expand-btn">{expandedStep === step.id ? '−' : '+'}</button>
              </div>

              {expandedStep === step.id && (
                <div className="step-content">
                  {step.content && <p>{step.content}</p>}
                  
                  {step.criteria && (
                    <div className="criteria-list">
                      <h5>Review Criteria:</h5>
                      <ul>
                        {step.criteria.map((criterion, index) => (
                          <li key={index}>{criterion}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {step.decisions && (
                    <div className="decisions-list">
                      <h5>Possible Decisions:</h5>
                      {step.decisions.map((decision, index) => (
                        <div key={index} className="decision-item">
                          <h6>{decision.title}</h6>
                          <p>{decision.desc}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="process-info">
          <div className="info-box">
            <h4>Quality Assurance</h4>
            <p>Our rigorous peer review process ensures that only high-quality, original research is presented at ICETTACS 2026.</p>
          </div>
          <div className="info-box">
            <h4>Ethical Standards</h4>
            <p>We maintain the highest ethical standards, including plagiarism detection and AI-generated content screening.</p>
          </div>
          <div className="info-box">
            <h4>Transparent Process</h4>
            <p>Double-anonymous reviews ensure fairness and impartiality in the evaluation of all submissions.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PeerReviewProcess;
